/**
 * @file Dropdown.js
 * @version 1.4.0
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2025-09-21
 * @modified 2026-10-12
 *
 * 1.4.0 (2026-10-12): an option may be `disabled` with a `title` — shown
 * greyed with its reason, and neither a click nor the keyboard takes it.
 */

import React, { useState, useRef, useEffect } from 'react';
import './Dropdown.css';
import useDropdownPosition from '../hooks/useDropdownPosition';
import useDropdownKeys from '../hooks/useDropdownKeys';

// `escapeOverflow` takes the menu out of a clipping scroll ancestor by pinning
// it with position:fixed — needed inside the order-lines grid, whose horizontal
// scroll makes the browser clip vertically too (overflow-x:auto computes
// overflow-y to auto, not visible). Same contract as SearchableDropdown's, and
// off by default so every existing caller behaves exactly as before.
function Dropdown({ options, value, onChange, placeholder = "Select...", disabled,
  compact = false, boundarySelector = null, escapeOverflow = false }) {
  const [isOpen, setIsOpen] = useState(false);
  // The keyboard cursor — NOT the selected value, which `.active` marks. See
  // hooks/useDropdownKeys.
  const [cursor, setCursor] = useState(0);
  const dropdownRef = useRef(null);
  const menuRef = useRef(null);
  const triggerRef = useRef(null);
  const { flipUp, theHeight, menuStyle } = useDropdownPosition(dropdownRef, isOpen, {
    boundarySelector, escapeOverflow, reflowOnScroll: escapeOverflow,
  });

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // On open, scroll the currently-selected option into view (centred) so the
  // user can see and change their current selection without hunting for it.
  useEffect(() => {
    if (!isOpen) return undefined;
    const id = requestAnimationFrame(() => {
      const menu = menuRef.current;
      const active = menu && menu.querySelector('.dropdown-item.active');
      if (!menu || !active) return;
      const a = active.getBoundingClientRect();
      const m = menu.getBoundingClientRect();
      menu.scrollTop += (a.top - m.top) - (m.height - a.height) / 2;
    });
    return () => cancelAnimationFrame(id);
  }, [isOpen]);

  const selectedOption = options.find(opt => opt.value === value);

  // No search box here, so focus never leaves the trigger: it handles the
  // closed keys and the open ones.
  const { onTriggerKeyDown, onMenuKeyDown } = useDropdownKeys({
    isOpen,
    setIsOpen,
    navigable: options,
    cursor,
    setCursor,
    onChoose: (option) => { if (!option.disabled) onChange(option.value); },
    triggerRef,
    listRef: menuRef,
    indexOfValue: () => options.findIndex(o => o.value === value),
    spaceChooses: true,
  });

  return (
    <div className={`dropdown ${compact ? 'compact' : ''} ${flipUp ? 'flip-up' : ''} ${escapeOverflow ? 'escaped' : ''}`} ref={dropdownRef}>
      <button
        ref={triggerRef}
        className="dropdown-trigger"
        onClick={() => {
          if (disabled) return;
          if (!isOpen) setCursor(Math.max(0, options.findIndex(o => o.value === value)));
          setIsOpen(!isOpen);
        }}
        onKeyDown={isOpen ? onMenuKeyDown : onTriggerKeyDown}
        type="button"
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
      >
        <span>{selectedOption?.label || placeholder}</span>
        <span className="material-symbols-rounded">{isOpen ? 'expand_less' : 'expand_more'}</span>
      </button>

      {isOpen && (
        <div
          className="dropdown-menu"
          ref={menuRef}
          style={{
            ...(menuStyle || {}),
            ...(theHeight ? { height: `${theHeight}px` } : {}),
            // Escaped menus are positioned by the hook after a measure pass;
            // hide the frame before it lands rather than flashing at the
            // unpositioned coordinates.
            ...(escapeOverflow && !menuStyle ? { visibility: 'hidden' } : {}),
          }}
        >
          {options.map((option, index) => (
            <div
              key={option.value}
              className={`dropdown-item ${value === option.value ? 'active' : ''} ${cursor === index ? 'is-cursor' : ''} ${option.disabled ? 'is-disabled' : ''}`}
              title={option.title || undefined}
              aria-disabled={option.disabled ? 'true' : undefined}
              data-cursor={cursor === index ? 'true' : undefined}
              // Mouse and keyboard share one highlight, so arrowing on from
              // wherever the pointer last rested does not jump.
              onMouseEnter={() => setCursor(index)}
              onClick={() => {
                if (option.disabled) return;
                onChange(option.value);
                setIsOpen(false);
              }}
            >
              {option.label}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default Dropdown;
